import React, { useState, useRef, useEffect, useContext } from "react";
import WindowHeader from "../../../Layout/WindowHeader/WindowHeader";
import ActionToolbar from "../../../NavBars/ActionToolbar";
import SherComboBox from "../../../ComboBoxes/SherComboBox";
import headeLogo from "../../../../assets/images/shersoftnavLogo.png";
import { HiRefresh } from "react-icons/hi";
import { IoSettingsOutline } from "react-icons/io5";
import { IoMdArrowDropdown } from "react-icons/io";
import { FiSearch, FiInfo, FiSliders } from "react-icons/fi";
import axios from "axios";
import { useDbContext } from "../../../../context/DbContext";
import { AuthContext } from "../../../../context/AuthContext";
import io from "socket.io-client";

function OtherRegistration({ onClose, zIndex }) {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const { dbCode } = useDbContext();
  const { agentCode } = useContext(AuthContext);

  const [dataFound, setDataFound] = useState(false);
  const [saveDisabled, setSaveDisabled] = useState(false);
  const [editDisabled, setEditDisabled] = useState(true);
  const [deleteDisabled, setDeleteDisabled] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    slNo: "",
    name: "",
    description: "",
    type: "",
    active: true,
    auto: "",
  });

  // Search input state
  const [searchQuery, setSearchQuery] = useState("");

  // Refs for navigation
  const nameInputRef = useRef(null);
  const descInputRef = useRef(null);
  const typeInputRef = useRef(null);
  const activeInputRef = useRef(null);
  const saveBtnRef = useRef(null);
  const editButtonRef = useRef(null);

  const updateNavRefs = (refs) => {
    if (refs.saveButtonRef) {
      saveBtnRef.current = refs.saveButtonRef.current;
    } else if (refs.saveBtnRef) {
      saveBtnRef.current = refs.saveBtnRef.current;
    }
    if (refs.editButtonRef) {
      editButtonRef.current = refs.editButtonRef.current;
    }
  };

  const fieldRefMap = {
    name: nameInputRef,
    description: descInputRef,
    type: typeInputRef,
    active: activeInputRef,
  };

  const fieldOrder = ["name", "description", "type", "active"];

  const focusFieldById = (id, attempt = 0) => {
    const ref = fieldRefMap[id];
    if (!ref) return;

    requestAnimationFrame(() => {
      if (ref.current && typeof ref.current.focus === "function") {
        ref.current.focus();
      } else if (attempt < 15) {
        setTimeout(() => focusFieldById(id, attempt + 1), 25);
      }
    });
  };

  const focusSaveOrEdit = () => {
    const isSaveEnabled = !(dataFound || saveDisabled);
    const isEditEnabled = !(!dataFound || editDisabled);

    if (isSaveEnabled && saveBtnRef.current) {
      saveBtnRef.current.focus();
    } else if (isEditEnabled && editButtonRef.current) {
      editButtonRef.current.focus();
    }
  };

  const handleEnterNav = (event, currentId) => {
    if (event.key !== "Enter" && event.key !== "Return") return;
    event.preventDefault();

    const idx = fieldOrder.indexOf(currentId);
    if (idx === -1) return;

    const nextId = fieldOrder[idx + 1];
    if (nextId) {
      focusFieldById(nextId);
    } else {
      focusSaveOrEdit();
    }
  };

  // Database records
  const [records, setRecords] = useState([]);

  // Fetch records from database
  const fetchRecords = async () => {
    if (!dbCode) return;
    try {
      const response = await axios.get(
        `${apiBaseUrl}/otherregistration/getoptions/${dbCode}`,
      );
      setRecords(response.data || []);
    } catch (error) {
      console.error("Error fetching records:", error.message);
    }
  };

  // Fetch next Sl No
  const fetchSlNo = async () => {
    if (!dbCode) return;
    try {
      const response = await axios.get(
        `${apiBaseUrl}/otherregistration/getslno/${dbCode}`,
      );
      setFormData((prev) => ({ ...prev, slNo: String(response.data) }));
    } catch (error) {
      console.error("Error fetching serial number:", error.message);
    }
  };

  useEffect(() => {
    if (dbCode) {
      fetchRecords();
      fetchSlNo();
    }

    const socket = io(apiBaseUrl);
    socket.on("OtherRegistrationUpdated", () => {
      fetchRecords();
    });

    return () => socket.disconnect();
  }, [dbCode, apiBaseUrl]);

  // Dropdown options for Name search combo
  const nameOptions = records.map((rec) => ({
    label: rec.name || rec.Name || "",
    value: String(rec.auto || rec.Auto || ""),
  }));

  const typeOptions = [
    { label: "Unit", value: "Unit" },
    { label: "Category", value: "Category" },
    { label: "Sub category", value: "Sub category" },
    { label: "Rack", value: "Rack" },
    { label: "Mfr", value: "Mfr" },
    { label: "Route", value: "Route" },
    { label: "City", value: "City" },
    { label: "Discount", value: "Discount" },
    { label: "Counter", value: "Counter" },
    { label: "Cost Center", value: "Cost Center" },
    { label: "Offer Type", value: "Offer Type" },
    { label: "Franchisee", value: "Franchisee" },
    { label: "Scheme", value: "Scheme" },
    { label: "Brand", value: "Brand" },
    { label: "SubGroup", value: "SubGroup" },
    { label: "Currency", value: "Currency" },
    { label: "Employee", value: "Employee" },
    { label: "Location", value: "Location" },
  ];

  // Search Filter
  const filteredRecords = records.filter((rec) => {
    const query = searchQuery.toLowerCase();
    const nameVal = (rec.name || rec.Name || "").toLowerCase();
    const descVal = (rec.Description || rec.description || "").toLowerCase();
    const typeVal = (rec.Type || rec.type || "").toLowerCase();
    return (
      nameVal.includes(query) ||
      descVal.includes(query) ||
      typeVal.includes(query)
    );
  });

  const handleClear = () => {
    setFormData({
      slNo: "",
      name: "",
      description: "",
      type: "",
      active: true,
      auto: "",
    });
    setDataFound(false);
    setSaveDisabled(false);
    setEditDisabled(true);
    setDeleteDisabled(true);
    fetchSlNo();
    setTimeout(() => {
      nameInputRef.current?.focus?.();
    }, 100);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    handleClear();
    if (dbCode) {
      try {
        await Promise.all([fetchRecords(), fetchSlNo()]);
      } catch (error) {
        console.error("Error refreshing data:", error.message);
      }
    }
    setTimeout(() => {
      setIsRefreshing(false);
    }, 600);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.type) {
      alert("Enter Data Correctly");
      return;
    }
    setSaveDisabled(true);
    try {
      const response = await axios.post(
        `${apiBaseUrl}/otherregistration/insert/${dbCode}`,
        {
          name: formData.name,
          description: formData.description,
          type: formData.type,
          takeuser: agentCode || 0,
        },
      );
      if (response.data.success) {
        alert("Record Saved successfully!");
        handleClear();
        fetchRecords();
      } else {
        alert(response.data.message || "Failed to save record.");
        setSaveDisabled(false);
      }
    } catch (error) {
      console.error("Error saving record:", error.message);
      alert("Failed to save record.");
      setSaveDisabled(false);
    }
  };

  const handleUpdate = async () => {
    if (!formData.auto) return;
    if (!formData.name || !formData.type) {
      alert("Enter Data Correctly");
      return;
    }
    try {
      const response = await axios.post(
        `${apiBaseUrl}/otherregistration/update/${dbCode}`,
        {
          auto: formData.auto,
          name: formData.name,
          description: formData.description,
          type: formData.type,
          active: formData.active,
          takeuser: agentCode || 0,
        },
      );
      if (response.data.success) {
        alert("Record Updated successfully!");
        handleClear();
        fetchRecords();
      } else {
        alert(response.data.message || "Failed to update record.");
      }
    } catch (error) {
      console.error("Error updating record:", error.message);
      alert("Failed to update record.");
    }
  };

  const handleDelete = async () => {
    if (!formData.auto) return;
    if (!window.confirm("Do you want to delete this record?")) return;
    try {
      const response = await axios.delete(
        `${apiBaseUrl}/otherregistration/delete/${dbCode}/${formData.auto}`,
        {
          params: {
            name: formData.name,
            type: formData.type,
            takeuser: agentCode || 0,
          },
        },
      );
      if (response.data.success) {
        alert("Record Deleted successfully!");
        handleClear();
        fetchRecords();
      } else {
        alert(response.data.message || "Failed to delete record.");
      }
    } catch (error) {
      console.error("Error deleting record:", error.message);
      alert("Failed to delete record.");
    }
  };

  const handleFind = async () => {
    if (!formData.name) {
      alert("Please enter or select a name to find.");
      return;
    }
    try {
      const response = await axios.get(
        `${apiBaseUrl}/otherregistration/find/${dbCode}`,
        {
          params: { name: formData.name },
        },
      );
      const foundData = response.data[0];
      if (foundData) {
        setFormData({
          slNo: String(foundData.auto || foundData.Auto || ""),
          name: foundData.name || foundData.Name || "",
          description: foundData.Description || foundData.description || "",
          type: foundData.Type || foundData.type || "",
          active: foundData.active !== 0,
          auto: foundData.auto || foundData.Auto || "",
        });
        setDataFound(true);
        setSaveDisabled(true);
        setEditDisabled(false);
        setDeleteDisabled(false);
      } else {
        alert("No record found with that name.");
      }
    } catch (error) {
      console.error("Error finding record:", error.message);
      alert("Error finding record.");
    }
  };

  const handleRowClick = (record) => {
    setFormData({
      slNo: String(record.auto || record.Auto || ""),
      name: record.name || record.Name || "",
      description: record.Description || record.description || "",
      type: record.Type || record.type || "",
      active: record.active !== 0,
      auto: record.auto || record.Auto || "",
    });
    setDataFound(true);
    setSaveDisabled(true);
    setEditDisabled(false);
    setDeleteDisabled(false);
  };

  return (
    <div
      className="absolute inset-0 w-full h-full bg-[#f0f0f0] flex items-center justify-center pointer-events-auto"
      style={{ zIndex: zIndex || 101 }}
    >
      <div className="bg-white font-Inter w-[99%] max-w-[1440px] h-[99%] max-h-[850px] border-none shadow-md flex flex-col items-center absolute overflow-hidden rounded-xl">
        <WindowHeader
          title="Other Registration"
          logo={headeLogo}
          onClose={onClose}
          closeBtnClassName="w-6 h-6 border-none rounded-[5px] mr-2"
        />

        {/* Settings / Sub bar */}
        <div className="w-full flex-shrink-0 h-9 bg-[#EAF0FC] border-b border-gray-200 flex items-center gap-2 px-2 box-border text-xs text-gray-600 select-none">
          <button className="flex items-center justify-center gap-1 border-none bg-transparent h-full w-auto text-gray-600 hover:text-gray-800 transition-colors cursor-pointer">
            <IoSettingsOutline className="w-[18px] h-[18px]" />
            <IoMdArrowDropdown className="w-[14px] h-[14px]" />
          </button>

          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex items-center gap-1 border-none bg-transparent text-[#1a3abf] hover:text-blue-800 font-medium cursor-pointer focus:outline-none disabled:opacity-50"
            type="button"
          >
            <HiRefresh className={`w-3.5 h-3.5 text-[#1a3abf] ${isRefreshing ? "animate-spin" : "transition-transform duration-500 hover:rotate-180"}`} />
            <span>{isRefreshing ? "Refreshing..." : "Refresh"}</span>
          </button>
        </div>

        {/* Toolbar */}
        <div className="w-full flex-shrink-0">
          <ActionToolbar
            width="100%"
            height="auto"
            bgColor="#EAF0FC"
            showSave={true}
            showPrint={false}
            showClear={true}
            showFind={true}
            showEdit={true}
            showDelete={true}
            showExit={true}
            showNavigation={false}
            saveDisabled={dataFound || saveDisabled}
            editDisabled={!dataFound || editDisabled}
            deleteDisabled={!dataFound || deleteDisabled}
            dataFound={dataFound}
            onSave={handleSubmit}
            onClear={handleClear}
            onFind={handleFind}
            onEdit={handleUpdate}
            onDelete={handleDelete}
            onExit={onClose}
            onRefsUpdate={updateNavRefs}
          />
        </div>

        {/* Body Layout */}
        <div className="w-full flex-1 overflow-y-auto flex bg-[#f1f5f9] p-3 gap-5 items-start justify-center">
          <div className="w-[48%] h-[400px] bg-white p-4 box-border flex flex-col gap-3.5 border border-gray-200 rounded-[8px] shadow-sm overflow-y-auto scrollbar">
            <div className="flex items-center gap-3 border-b border-gray-100 pb-2">
              <div className="w-9 h-9 bg-[#1a3abf] rounded-[6px] flex items-center justify-center text-white shrink-0 shadow-sm">
                <FiSliders className="w-[20px] h-[20px]" />
              </div>
              <div className="flex flex-col">
                <h2 className="text-[15px] font-bold text-[#1e293b] m-0">
                  Other Registration
                </h2>
                <span className="text-[12px] text-[#64748b] mt-0.5">
                  Fill in the other registration information below
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-2 max-w-[500px]">
              {/* Sl No */}
              <div className="flex items-center gap-4">
                <label className="text-[13px] font-semibold text-[#334155] w-24 shrink-0">
                  Sl No <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.slNo}
                  disabled
                  className="w-32 px-3 text-[13px] border border-[#cbd5e1] rounded-[4px] bg-[#f8fafc] text-gray-500 font-medium outline-none box-border"
                  style={{ height: "34px" }}
                />
              </div>

              {/* Name */}
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-4">
                  <label className="text-[13px] font-semibold text-[#334155] w-24 shrink-0">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <div className="flex-1">
                    <SherComboBox
                      comboRef={nameInputRef}
                      options={nameOptions}
                      value={formData.name}
                      onSelect={(opt) =>
                        setFormData({ ...formData, name: opt.label })
                      }
                      onInputChange={(text) =>
                        setFormData({ ...formData, name: text })
                      }
                      onFreeInput={(text) =>
                        setFormData({ ...formData, name: text })
                      }
                      placeholder="Select or enter name..."
                      showArrow={true}
                      clearable={false}
                      inputWidth="100%"
                      inputHeight="34px"
                      inputClassName="border border-[#cbd5e1] rounded-[4px]"
                      inputStyle={{ fontSize: "13px", paddingLeft: "8px" }}
                      onKeyDown={(e) => handleEnterNav(e, "name")}
                    />
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="flex items-center gap-4">
                <label className="text-[13px] font-semibold text-[#334155] w-24 shrink-0">
                  Description
                </label>
                <input
                  type="text"
                  placeholder="Enter description..."
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  className="flex-1 px-3 text-[13px] border border-[#cbd5e1] rounded-[4px] outline-none focus:border-[#1a3abf] box-border"
                  style={{ height: "34px" }}
                  ref={descInputRef}
                  onKeyDown={(e) => handleEnterNav(e, "description")}
                />
              </div>

              {/* Type */}
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-4">
                  <label className="text-[13px] font-semibold text-[#334155] w-24 shrink-0">
                    Type
                  </label>
                  <div className="flex-1">
                    <SherComboBox
                      comboRef={typeInputRef}
                      options={typeOptions}
                      value={formData.type}
                      onSelect={(opt) =>
                        setFormData({ ...formData, type: opt.label })
                      }
                      onInputChange={(text) =>
                        setFormData({ ...formData, type: text })
                      }
                      onFreeInput={(text) =>
                        setFormData({ ...formData, type: text })
                      }
                      placeholder="Select type..."
                      showArrow={true}
                      clearable={false}
                      inputWidth="100%"
                      inputHeight="34px"
                      inputClassName="border border-[#cbd5e1] rounded-[4px]"
                      inputStyle={{ fontSize: "13px", paddingLeft: "8px" }}
                      onKeyDown={(e) => handleEnterNav(e, "type")}
                    />
                  </div>
                </div>
              </div>

              {/* Active */}
              <div className="flex items-center gap-4 mt-0.5">
                <div className="w-24 shrink-0"></div>
                <label className="flex items-center gap-2 text-[13px] text-gray-700 font-semibold cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) =>
                      setFormData({ ...formData, active: e.target.checked })
                    }
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    ref={activeInputRef}
                    onKeyDown={(e) => handleEnterNav(e, "active")}
                  />
                  Active
                </label>
              </div>
            </div>

            {/* Info Box */}
            <div className="mt-3 bg-blue-50 border border-blue-200 rounded-[6px] p-2.5 flex gap-2.5 text-blue-700 max-w-[500px]">
              <FiInfo className="w-4.5 h-4.5 shrink-0 mt-0.5" />
              <div className="text-[12px] leading-relaxed">
                Select the correct type for this entry. This affects filtering
                and reporting across the system.
              </div>
            </div>
          </div>

          {/* Right panel - List of Registered Records */}
          <div className="w-[48%] h-[400px] flex flex-col border border-gray-200 rounded-[8px] shadow-sm overflow-hidden bg-white">
            {/* Header */}
            <div className="w-full flex-shrink-0 h-11 bg-[#1a409f] flex items-center justify-between px-4 box-border text-white select-none">
              <span className="text-[14px] font-bold flex items-center gap-2">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="8" y1="6" x2="21" y2="6" />
                  <line x1="8" y1="12" x2="21" y2="12" />
                  <line x1="8" y1="18" x2="21" y2="18" />
                  <line x1="3" y1="6" x2="3.01" y2="6" />
                  <line x1="3" y1="12" x2="3.01" y2="12" />
                  <line x1="3" y1="18" x2="3.01" y2="18" />
                </svg>
                Registered Records
              </span>
              <span className="bg-white text-[#1a409f] font-bold text-xs px-2 py-0.5 rounded-full shrink-0">
                {records.length}
              </span>
            </div>

            {/* Search Bar */}
            <div className="p-3 border-b border-gray-100 flex-shrink-0 flex items-center bg-gray-50 gap-2">
              <FiSearch className="text-gray-400 w-4 h-4 shrink-0" />
              <input
                type="text"
                placeholder="Search records..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 bg-transparent border-none outline-none text-[13.5px] text-gray-700"
              />
            </div>

            {/* Table Area */}
            <div className="flex-1 overflow-y-auto min-h-0 scrollbar">
              <table className="w-full border-collapse text-left text-[13px]">
                <thead>
                  <tr className="bg-gray-100 border-b border-gray-200 text-gray-500 font-semibold sticky top-0 z-10">
                    <th className="p-3 w-1/3">Name</th>
                    <th className="p-3 w-1/3">Description</th>
                    <th className="p-3 w-1/3">Type</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredRecords.map((rec, index) => (
                    <tr
                      key={rec.auto || rec.Auto || index}
                      onClick={() => handleRowClick(rec)}
                      className={`hover:bg-blue-50 cursor-pointer transition-colors ${
                        formData.name === rec.name || formData.name === rec.Name
                          ? "bg-blue-50/50"
                          : ""
                      }`}
                    >
                      <td className="p-3 font-medium text-gray-900">
                        {rec.name || rec.Name || ""}
                      </td>
                      <td className="p-3 text-gray-500">
                        {rec.Description || rec.description || "-"}
                      </td>
                      <td className="p-3">
                        <span className="inline-block px-2 py-0.5 text-[11px] font-semibold rounded-full bg-blue-50 text-[#1a3abf] border border-blue-100">
                          {rec.Type || rec.type || ""}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {filteredRecords.length === 0 && (
                    <tr>
                      <td colSpan="3" className="p-4 text-center text-gray-400">
                        No records found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <style
          dangerouslySetInnerHTML={{
            __html: `
          * { font-family: 'Inter', sans-serif !important; }
          .scrollbar::-webkit-scrollbar { width: 5px; height: 5px; }
          .scrollbar::-webkit-scrollbar-track { background: #f1f5f9; }
          .scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
          .scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
        `,
          }}
        />
      </div>
    </div>
  );
}

export default OtherRegistration;
